/**
 * useEventMonitorPolling — FEAT-MONITOR-001 (Events Monitor)
 * Batches the three event-monitor fetches (overview + throughput + by-customer) on one timer.
 * Keeps last-good payload + marks `stale` on a failed poll (dims, never blanks). Throughput +
 * by-customer reload immediately on window/bucket/account change. Same shape as the message
 * monitor's useMonitorPolling.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWrapper } from "../../helpers";

// Admin-monitor is its OWN service (e.g. https://adminmonitor.xnotify.com); it aggregates the
// per-box metrics agents. Falls back to the general API URL only for local dev.
const BASE = process.env.REACT_APP_MONITOR_API_URL || process.env.REACT_APP_API_URL;

export default function useEventMonitorPolling({ intervalMs = 10000, window = "6h", bucket = "5m", customerId = null }) {
  const [overview, setOverview] = useState(null);
  const [throughput, setThroughput] = useState(null);
  const [byCustomer, setByCustomer] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [lastOkAt, setLastOkAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const cfg = useRef({ window, bucket, customerId });
  cfg.current = { window, bucket, customerId };
  const byCustTick = useRef(0);

  const get = useCallback((path) => fetchWrapper.get(`${BASE}${path}`), []);
  const scope = useCallback((path) => {
    const cid = cfg.current.customerId;
    const sep = path.includes("?") ? "&" : "?";
    return cid ? `${path}${sep}customer_id=${cid}` : path;
  }, []);

  const tick = useCallback(async () => {
    setBusy(true);
    let anyOk = false;
    try {
      const { window: w, bucket: b } = cfg.current;
      const ovP = get(scope(`/admin/monitor/events/overview?window=${w}`))
        .then((r) => (r?.success !== false ? r.data : null)).catch(() => null);
      const thP = get(scope(`/admin/monitor/events/throughput?window=${w}&bucket=${b}`))
        .then((r) => (r?.success !== false ? r.data : { _error: r })).catch((e) => ({ _error: e }));
      // by-customer is platform-wide (top emitters) → not customer-scoped, every 3rd tick.
      const dueBc = byCustTick.current % 3 === 0;
      const bcP = dueBc
        ? get(`/admin/monitor/events/by-customer?window=${w}`).then((r) => (r?.success !== false ? r.data : null)).catch(() => null)
        : Promise.resolve(undefined);
      byCustTick.current += 1;

      const plP = get(`/admin/monitor/events/pipeline`).then((r) => (r?.success !== false ? r.data : null)).catch(() => null);

      const [ov, th, bc, pl] = await Promise.all([ovP, thP, bcP, plP]);
      if (ov) { setOverview(ov); anyOk = true; }
      if (th && !th._error) { setThroughput(th); anyOk = true; }
      if (bc !== undefined && bc) { setByCustomer(bc); anyOk = true; }
      if (pl) { setPipeline(pl); anyOk = true; }

      if (anyOk) { setStale(false); setLastOkAt(new Date().toISOString()); setError(null); }
      else { setStale(true); setError("Couldn't reach event monitor API"); }
    } catch (e) {
      setStale(true); setError(e?.message || "Couldn't reach event monitor API");
    } finally {
      setBusy(false);
    }
  }, [get, scope]);

  useEffect(() => {
    tick();
    if (!intervalMs) return undefined;
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [tick, intervalMs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await get(scope(`/admin/monitor/events/throughput?window=${window}&bucket=${bucket}`));
        if (!cancelled && r?.success !== false) setThroughput(r.data);
      } catch (_) { /* keep old series */ }
    })();
    return () => { cancelled = true; };
  }, [window, bucket, customerId, get, scope]);

  return { overview, throughput, byCustomer, pipeline, error, stale, lastOkAt, busy, refresh: tick };
}
